import {
    Box,
    Card,
    Divider,
    LinearProgress,
    Table,
    TableBody,
    TableBodyProps,
    TableCell,
    TableCellProps,
    TableContainer,
    TableContainerProps,
    TableHead,
    TableHeadProps,
    TablePagination,
    TablePaginationProps,
    TableProps,
    TableRow,
    TableRowProps,
} from '@mui/material';
import {alpha, useTheme} from '@mui/material/styles';
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Header,
    SortingState,
    useReactTable,
    VisibilityState,
} from '@tanstack/react-table';
import {ScrollX} from 'components/Theme';
import {Fragment, ReactNode, useEffect, useMemo, useState} from 'react';
import {AppTableColumnSorting, AppTableColumnVisibilityToggleMenu} from './components';
import ApplinearLoader from 'components/loaders/ApplinearLoader';

interface RowCellProps {
    tableRowProps?: TableRowProps;
    tableCellProps?: TableCellProps;
}

interface AppTableProps<T extends {id?: string}> {
    columns: ColumnDef<T>[];
    data: T[];
    loading?: boolean;
    showPagination?: boolean;
    pageCount?: number;
    pageIndex?: number;
    pageSize?: number;
    rowsPerPageOptions?: number[];
    onPageChange?: (newPage: number) => void;
    onPageSizeChange?: (newSize: number) => void;
    PreviewSection?: ReactNode;
    handleRowClick?: (row: T) => void;
    renderHeaderCell?: (headerCell: Header<T, unknown>) => ReactNode;
    SearchInput?: ReactNode;
    tableContainerProps?: TableContainerProps;
    tableProps?: TableProps;
    tableHeadProps?: TableHeadProps & RowCellProps;
    tableBodyProps?: TableBodyProps & RowCellProps;
    enableColumnResizing?: boolean;
    tablePaginationProps?: TablePaginationProps;
    showColumnVisibilityToggle?: boolean;
    renderLeftHeader?: ReactNode;
    onHandleSortChange?: (sortValue: 'asc' | 'desc' | false, columnId: string) => void;
    onMultiRowSelectedChange?: (value: T[]) => void;
    defaultColumnVisibility?: VisibilityState;
}

export function AppTable<T extends {id?: string}>({
    columns,
    data,
    loading = false,
    showPagination = false,
    pageCount = 1,
    pageIndex = 0,
    pageSize = 10,
    onPageChange,
    onPageSizeChange,
    enableColumnResizing = false,
    SearchInput,
    PreviewSection,
    tableContainerProps,
    tableProps,
    rowsPerPageOptions = [5, 10, 25, 50],
    renderHeaderCell,
    renderLeftHeader,
    tableHeadProps,
    tableBodyProps,
    onHandleSortChange,
    tablePaginationProps,
    showColumnVisibilityToggle = false,
    defaultColumnVisibility = {},

    onMultiRowSelectedChange,
}: AppTableProps<T>) {
    const theme = useTheme();
    const [sorting, setSorting] = useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [rowSelection, setRowSelection] = useState({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnSizing, setColumnSizing] = useState({});

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
            columnSizing,
            rowSelection,
            columnVisibility: columnVisibility,
            // if not manual pagination, show all rows
            ...(showPagination ? {} : {pagination: {pageIndex: 0, pageSize: data.length}}),
        },
        manualPagination: showPagination,
        pageCount: showPagination ? pageCount : undefined,
        onPaginationChange: showPagination
            ? updater => {
                  const newState = typeof updater === 'function' ? updater({pageIndex, pageSize}) : updater;
                  onPageChange?.(newState.pageIndex);
                  onPageSizeChange?.(newState.pageSize);
              }
            : undefined,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        enableColumnResizing: enableColumnResizing,
        columnResizeMode: 'onChange',
        onColumnSizingChange: setColumnSizing,
        onColumnVisibilityChange: setColumnVisibility,
    });
    const selectedRows = useMemo(() => {
        return table.getSelectedRowModel().flatRows.map(row => row.original);
    }, [table.getSelectedRowModel().flatRows]);
    useEffect(() => {
        if (onMultiRowSelectedChange) {
            onMultiRowSelectedChange(selectedRows);
        }
    }, [selectedRows]);
    const {
        tableRowProps: tableHeadRowProps,
        tableCellProps: tableHeadCellProps,
        ...restTableHeadProps
    } = tableHeadProps ?? {};
    const {
        tableRowProps: tableBodyRowProps,
        tableCellProps: tableBodyCellProps,
        ...restTableBodyProps
    } = tableBodyProps ?? {};

    const backColor = alpha(theme.palette.primary.lighter, 0.1);

    return (
        <Card sx={{position: 'relative', width: '100%'}}>
            <Box display={'flex'} justifyContent="space-between" alignItems="center">
                {renderLeftHeader ?? <Box></Box>}
                <Box display={'flex'} justifyContent="end" alignItems="center" sx={{p: 1}}>
                    {SearchInput && <Box sx={{p: 2}}>{SearchInput}</Box>}
                    {showColumnVisibilityToggle && (
                        <AppTableColumnVisibilityToggleMenu
                            columns={table.getAllLeafColumns().filter(col => col.columnDef.enableHiding)}
                        />
                    )}
                </Box>
            </Box>
            <Divider />
            <ScrollX>
                <TableContainer {...tableContainerProps}>
                    <Table {...tableProps}>
                        <TableHead {...restTableHeadProps}>
                            {table.getHeaderGroups().map(hg => (
                                <TableRow {...tableHeadRowProps} key={hg.id}>
                                    {hg.headers.map(header => (
                                        <TableCell
                                            key={header.id}
                                            {...tableHeadCellProps}
                                            style={{
                                                width: header.getSize(),
                                                minWidth: header.getSize(),
                                                maxWidth: header.getSize(),
                                                paddingTop: 0,
                                                paddingBottom: 0,
                                                padding: 0,
                                            }}
                                        >
                                            <Box display={'flex'} justifyContent="space-between" alignItems="center">
                                                <Box display={'flex'} alignItems="center">
                                                    {header.column.getCanSort() && (
                                                        <AppTableColumnSorting
                                                            onSortChangeHandler={sortValue => {
                                                                onHandleSortChange &&
                                                                    onHandleSortChange(sortValue, header.column.id);
                                                            }}
                                                            column={header.column}
                                                        />
                                                    )}
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext(),
                                                          )}
                                                </Box>
                                                {renderHeaderCell && renderHeaderCell(header)}
                                            </Box>
                                            {header.column.getCanResize() && (
                                                <Box
                                                    onMouseDown={header.getResizeHandler()}
                                                    onTouchStart={header.getResizeHandler()}
                                                    sx={{
                                                        position: 'absolute',
                                                        right: 0,
                                                        top: 0,
                                                        height: '100%',
                                                        width: 6,
                                                        cursor: 'col-resize',
                                                        zIndex: 10,
                                                        userSelect: 'none',
                                                        touchAction: 'none',
                                                        background: header.column.getIsResizing()
                                                            ? theme.palette.primary.light
                                                            : 'transparent',
                                                        transition: 'background 0.2s',
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}

                            <div style={{position: 'absolute', width: '100%', height: '20px'}}>
                                {loading && <ApplinearLoader style={{width: '100%'}} />}
                            </div>
                        </TableHead>
                        <TableBody {...restTableBodyProps}>
                            {table.getRowModel().rows.length > 0 && (
                                <>
                                    {table.getRowModel().rows.map(row => (
                                        <Fragment key={row.id}>
                                            <TableRow {...tableBodyRowProps}>
                                                {row.getVisibleCells().map(cell => (
                                                    <TableCell
                                                        key={cell.id}
                                                        onClick={() => {
                                                            if (cell.column.columnDef.meta?.handleCellClick)
                                                                cell.column.columnDef.meta?.handleCellClick(row);
                                                        }}
                                                        {...tableBodyCellProps}
                                                        style={{
                                                            width: cell.column.getSize(),
                                                            minWidth: cell.column.getSize(),
                                                            maxWidth: cell.column.getSize(),
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere',
                                                            paddingLeft: 0,
                                                        }}
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>

                                            {row.getIsExpanded() && PreviewSection && (
                                                <TableRow
                                                    {...tableBodyRowProps}
                                                    sx={{
                                                        bgcolor: backColor,
                                                        '&:hover': {bgcolor: `${backColor} !important`},
                                                    }}
                                                >
                                                    <TableCell
                                                        {...tableBodyCellProps}
                                                        colSpan={row.getVisibleCells().length}
                                                        sx={{p: 2.5}}
                                                    >
                                                        {PreviewSection}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    ))}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {showPagination && onPageChange && onPageSizeChange && (
                    <Box
                        sx={{
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            px: 1,
                        }}
                    >
                        <TablePagination
                            component="div"
                            count={pageCount}
                            page={pageIndex}
                            rowsPerPage={pageSize}
                            onPageChange={(_, newPage) => onPageChange(newPage)}
                            onRowsPerPageChange={e => {
                                const newSize = parseInt(e.target.value, 10);
                                onPageSizeChange(newSize);
                                onPageChange(0);
                            }}
                            rowsPerPageOptions={rowsPerPageOptions}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </ScrollX>
        </Card>
    );
}

export default AppTable;
